import { create } from 'zustand';

type NestedAllocations = Record<string, Record<string, number>>;

interface FundingAllocationState {
  allocations: NestedAllocations;
  isDirty: boolean;
  activeQuoteId: string | null;
  policyCost: number;
  isDisabled: boolean;
}

interface FundingAllocationActions {
  setAllocation: (quoteId: string, sourceId: string, amount: number) => void;
  removeAllocation: (quoteId: string, sourceId: string) => void;
  clearAllocations: (quoteId: string) => void;
  initFromSession: (sessionAllocations: NestedAllocations) => void;
  markClean: () => void;
  setActiveQuote: (quoteId: string | null, policyCost: number) => void;
  setDisabled: (disabled: boolean) => void;
}

type FundingAllocationStore = FundingAllocationState & FundingAllocationActions;

export const useFundingAllocationStore = create<FundingAllocationStore>(set => ({
  allocations: {},
  isDirty: false,
  activeQuoteId: null,
  policyCost: 0,
  isDisabled: false,

  setAllocation: (quoteId, sourceId, amount) =>
    set(state => ({
      allocations: {
        ...state.allocations,
        [quoteId]: {
          ...(state.allocations[quoteId] ?? {}),
          [sourceId]: amount,
        },
      },
      isDirty: true,
    })),

  removeAllocation: (quoteId, sourceId) =>
    set(state => {
      const quoteAllocations = { ...(state.allocations[quoteId] ?? {}) };
      delete quoteAllocations[sourceId];
      return {
        allocations: {
          ...state.allocations,
          [quoteId]: quoteAllocations,
        },
        isDirty: true,
      };
    }),

  clearAllocations: quoteId =>
    set(state => {
      const updated = { ...state.allocations };
      delete updated[quoteId];
      return { allocations: updated, isDirty: true };
    }),

  initFromSession: sessionAllocations =>
    set({ allocations: sessionAllocations ?? {}, isDirty: false }),

  markClean: () => set({ isDirty: false }),

  setActiveQuote: (quoteId, policyCost) => set({ activeQuoteId: quoteId, policyCost }),

  setDisabled: disabled => set({ isDisabled: disabled }),
}));

export const useQuoteAllocations = (quoteId: string) =>
  useFundingAllocationStore(state => state.allocations[quoteId] ?? {});

export const useAllocation = (quoteId: string, sourceId: string) =>
  useFundingAllocationStore(state => state.allocations[quoteId]?.[sourceId] ?? 0);

export const useIsSourceEnabled = (quoteId: string, sourceId: string) =>
  useFundingAllocationStore(state => sourceId in (state.allocations[quoteId] ?? {}));

export const useTotalAllocated = (quoteId: string) =>
  useFundingAllocationStore(state =>
    Object.values(state.allocations[quoteId] ?? {}).reduce((sum, amt) => sum + (amt ?? 0), 0),
  );

export const useAllAllocations = () => useFundingAllocationStore(state => state.allocations);

export const useIsDirty = () => useFundingAllocationStore(state => state.isDirty);

export const useActiveQuoteId = () => useFundingAllocationStore(state => state.activeQuoteId);

export const usePolicyCost = () => useFundingAllocationStore(state => state.policyCost);

export const useIsDisabled = () => useFundingAllocationStore(state => state.isDisabled);

const calcTotalAllocatedForActiveQuote = (state: FundingAllocationState): number => {
  const { activeQuoteId, allocations } = state;
  if (!activeQuoteId) return 0;
  return Object.values(allocations[activeQuoteId] ?? {}).reduce((sum, amt) => sum + (amt ?? 0), 0);
};

export const useActiveTotalAllocated = () =>
  useFundingAllocationStore(calcTotalAllocatedForActiveQuote);

export const useRemainingBudget = () =>
  useFundingAllocationStore(state => {
    const totalAllocated = calcTotalAllocatedForActiveQuote(state);
    return Math.max(0, state.policyCost - totalAllocated);
  });

export const useIsFullyFunded = () =>
  useFundingAllocationStore(state => {
    if (!state.activeQuoteId) return false;
    const totalAllocated = calcTotalAllocatedForActiveQuote(state);
    return totalAllocated >= state.policyCost;
  });

export const getFundingAllocationActions = () => ({
  setAllocation: useFundingAllocationStore.getState().setAllocation,
  removeAllocation: useFundingAllocationStore.getState().removeAllocation,
  clearAllocations: useFundingAllocationStore.getState().clearAllocations,
  initFromSession: useFundingAllocationStore.getState().initFromSession,
  markClean: useFundingAllocationStore.getState().markClean,
  setActiveQuote: useFundingAllocationStore.getState().setActiveQuote,
  setDisabled: useFundingAllocationStore.getState().setDisabled,
});
